import { Fragment } from "react";
import { Pie, PieChart, Cell } from "recharts";
import { Target } from "lucide-react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { cn, message } from "@/lib/utils";
import { chartColors } from "@/lib/chart";
import type { ShareOfVoiceAPIData } from "@/schema/brand-os/brand-intel";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";


function PieContent({ data, highlightBrand }: { data: ShareOfVoiceAPIData; highlightBrand?: string }) {
  const pieData = data.entries.map((entry, index) => ({
    name: entry.brand_name,
    value: entry.relative_value,
    fill: chartColors[index % chartColors.length],
  }));

  const chartConfig = data.entries.reduce(
    (acc, entry, index) => ({
      ...acc,
      [entry.brand_name]: { label: entry.brand_name, color: chartColors[index % chartColors.length] },
    }),
    {} as ChartConfig
  );

  return (
    <Fragment>
      <ChartContainer config={chartConfig} className="mx-auto aspect-square w-full max-h-52 shrink-0">
        <PieChart accessibilityLayer>
          <ChartTooltip cursor={false} content={<ChartTooltipContent className="font-semibold" />} />
          <Pie data={pieData} dataKey="value" nameKey="name" innerRadius="60%" outerRadius="85%" label={false} strokeWidth={2} stroke="var(--background)">
            {pieData.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <ul className="mt-auto flex flex-col gap-2.5 pt-4">
        {data.entries.map((item, index) => {
          const isHighlighted = highlightBrand && item.brand_name === highlightBrand;
          return (
            <li key={item.brand_name} className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2">
                <div className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: chartColors[index % chartColors.length] }} aria-hidden />
                <p className={cn("truncate text-sm", isHighlighted ? "font-semibold text-destructive" : "text-foreground")}>{item.brand_name}</p>
              </div>
              <p className={cn("text-sm font-medium", isHighlighted ? "text-destructive" : "text-foreground")}>{Math.round(item.relative_value)}%</p>
            </li>
          );
        })}
      </ul>
    </Fragment>
  );
}

function ShareOfVoiceContent({ status, data, highlightBrand }: { status: QueryStatus; data: ShareOfVoiceAPIData | undefined; highlightBrand?: string }) {
  switch (status) {
    case "pending":
      return <ShareOfVoiceSkeleton />;
    case "error":
      return <LockedContent message={message} className="mt-14" />;
    case "success":
      return !data?.entries?.length ? <LockedContent message={message} className="mt-14" /> : <PieContent data={data} highlightBrand={highlightBrand} />;
  }
}

interface ShareOfVoiceChartProps {
  highlightBrand?: string;
}

export function ShareOfVoiceChart({ highlightBrand }: ShareOfVoiceChartProps) {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchShareOfVoice());

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="flex flex-row items-center gap-2">
        <div className="rounded-md bg-destructive/10 p-2">
          <Target className="size-5 text-destructive" strokeWidth={2.5} />
        </div>
        <CardTitle className="text-base font-semibold">Share of Voice</CardTitle>
      </CardHeader>
      <CardContent className="relative flex flex-1 flex-col pt-0">
        <ShareOfVoiceContent status={status} data={data} highlightBrand={highlightBrand} />
      </CardContent>
    </Card>
  );
}

function ShareOfVoiceSkeleton() {
  return (
    <div className="flex flex-col items-center gap-4">
      <Skeleton className="aspect-square w-full max-h-52 rounded-full" />
      <div className="flex w-full flex-col gap-2.5 pt-4">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Skeleton className="size-2.5 rounded-full" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-4 w-10" />
          </div>
        ))}
      </div>
    </div>
  );
}
